import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { anecdotesService, smasheursService, tournoisService } from '../services/api';

const Anecdotes = () => {
  const [anecdotes, setAnecdotes] = useState([]);
  const [smasheurs, setSmasheurs] = useState([]);
  const [tournois, setTournois] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [anecdotesRes, smasheursRes, tournoisRes] = await Promise.all([
        anecdotesService.getAll(),
        smasheursService.getAll(),
        tournoisService.getAll()
      ]);
      setAnecdotes(anecdotesRes.data);
      setSmasheurs(smasheursRes.data);
      setTournois(tournoisRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Parse le texte d'une anecdote et remplace les éléments entre accolades par des liens stylisés
   * Format supporté:
   * - {Nom du joueur} -> lien vers le profil du joueur
   * - {Saucismash#123} -> lien vers le tournoi
   */
  const parseNumSaucisse = (num) => {
    const s = String(num);
    if (s.includes('+')) {
      const [base, add] = s.split('+');
      return parseFloat(base) + parseFloat(add) * 0.1;
    }
    return parseFloat(s.replace(',', '.'));
  };

  const parseAnecdoteText = (text) => {
    // Regex pour trouver tout ce qui est entre accolades
    const regex = /\{([^}]+)\}/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      // Ajouter le texte avant le match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }

      const content = match[1]; // Le contenu entre accolades

      // Vérifier si c'est un tournoi (format: Saucismash#123, Saucismash#152.5, Saucismash#266+1)
      const tournoiMatch = content.match(/Saucismash\s*#([\d,.+]+)/i);
      if (tournoiMatch) {
        const rawNum = tournoiMatch[1];
        const numSaucisse = parseNumSaucisse(rawNum);
        const tournoi = tournois.find(t => parseNumSaucisse(t.numSaucisse) === numSaucisse);

        if (tournoi) {
          parts.push(
            <Link
              key={`tournoi-${match.index}`}
              to={`/tournois/${tournoi.id}`}
              className="badge bg-primary text-decoration-none mx-1"
            >
              Saucismash #{rawNum}
            </Link>
          );
        } else {
          parts.push(<span key={`tournoi-${match.index}`} className="badge bg-secondary mx-1">Saucismash #{rawNum}</span>);
        }
      } else {
        // Format {NomAffiché|idPlayerAPI} ou {Pseudo}
        const pipeIndex = content.indexOf('|');
        const displayName = pipeIndex !== -1 ? content.substring(0, pipeIndex) : content;
        const playerApiId = pipeIndex !== -1 ? parseInt(content.substring(pipeIndex + 1), 10) : null;

        let smasheur;
        if (playerApiId) {
          smasheur = smasheurs.find(s => s.idPlayerAPI === playerApiId);
        } else {
          smasheur = smasheurs.find(s =>
            s.pseudo.toLowerCase() === displayName.toLowerCase() ||
            (s.tag && s.tag.toLowerCase() === displayName.toLowerCase())
          );
        }

        if (smasheur) {
          parts.push(
            <Link
              key={`smasheur-${match.index}`}
              to={`/smasheurs/${smasheur.id}`}
              className="badge bg-warning text-dark text-decoration-none mx-1"
            >
              {displayName}
            </Link>
          );
        } else {
          parts.push(<span key={`unknown-${match.index}`} className="badge bg-secondary mx-1">{displayName}</span>);
        }
      }

      lastIndex = regex.lastIndex;
    }

    // Ajouter le reste du texte
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts;
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  // Séparer les anecdotes par type
  const byIdDesc = (a, b) => b.id - a.id;
  const meltdownAnecdotes = anecdotes.filter(a => a.type === 'Meltdown').sort(byIdDesc);
  const puzzleInnAnecdotes = anecdotes.filter(a => a.type === 'PuzzleInn').sort(byIdDesc);
  const gentlemanAnecdotes = anecdotes.filter(a => a.type === 'Gentleman et Barbecue').sort(byIdDesc);

  const renderAnecdotesList = (anecdotesList) => (
    <ul className="list-group list-group-flush">
      {anecdotesList.map((anecdote) => (
        <li
          key={anecdote.id}
          className="list-group-item text-bg-dark border-secondary"
        >
          <p className="pl-2 mb-0">
            <span className="me-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                className="bi bi-lightning-charge-fill"
                viewBox="0 0 16 16"
              >
                <path d="M11.251.068a.5.5 0 0 1 .227.58L9.677 6.5H13a.5.5 0 0 1 .364.843l-8 8.5a.5.5 0 0 1-.842-.49L6.323 9.5H3a.5.5 0 0 1-.364-.843l8-8.5a.5.5 0 0 1 .615-.09z" />
              </svg>
            </span>
            {parseAnecdoteText(anecdote.body)}
          </p>
        </li>
      ))}
    </ul>
  );

  return (
    <div>
      {anecdotes.length > 0 ? (
        <>
          <div className="hero-banner">
            <h1 className="hero-title">
              {anecdotes.length} {anecdotes.length > 1 ? 'Anecdotes' : 'Anecdote'}
            </h1>
            <p>et statistiques les plus juteuses de ta région</p>
          </div>

          <div className="container-xl">

            {/* Section PuzzleInn et Gentleman */}
            {(puzzleInnAnecdotes.length > 0 || gentlemanAnecdotes.length > 0) && (
              <div className="mb-5">
                <h2 className="text-center mb-4 mt-5">
                  <i className="bi bi-puzzle me-2"></i>
                  PuzzleInn & Gentleman
                  <span className="badge bg-warning text-dark ms-2">
                    {puzzleInnAnecdotes.length + gentlemanAnecdotes.length}
                  </span>
                </h2>

                {/* Sous-section PuzzleInn */}
                {puzzleInnAnecdotes.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-center mb-3">
                      <i className="bi bi-controller me-2"></i>
                      PuzzleInn
                    </h4>
                    {renderAnecdotesList(puzzleInnAnecdotes)}
                  </div>
                )}

                {/* Sous-section Gentleman et Barbecue */}
                {gentlemanAnecdotes.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-center mb-3 mt-4">
                      <i className="bi bi-suit-spade me-2"></i>
                      Gentleman et Barbecue
                    </h4>
                    {renderAnecdotesList(gentlemanAnecdotes)}
                  </div>
                )}
              </div>
            )}

            {/* Section Meltdown */}
            {meltdownAnecdotes.length > 0 && (
              <div className="mb-5">
                <h2 className="text-center mb-4 mt-4">
                  <i className="bi bi-shop me-2"></i>
                  Meltdown
                  <span className="badge bg-warning text-dark ms-2">{meltdownAnecdotes.length}</span>
                </h2>
                {renderAnecdotesList(meltdownAnecdotes)}
              </div>
            )}

          </div>
        </>
      ) : (
        <p className="h1 mt-5 text-center">Pas d'anecdote sorry bro</p>
      )}
    </div>
  );
};

export default Anecdotes;